import { useState, useCallback, useEffect, type PropsWithChildren } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeftSVG } from 'components/iconsSVG/chevronLeft';
import { ProgressiveImg } from 'components/base';
import { useI18n } from 'context/i18n';
import { dataLayerHandleEvent } from 'utils/dataLayers';

import styles from './style.module.css';
import { getDomainValue } from 'utils/domain';
import { Button } from 'components/ui/button';
import { Link } from 'components/ui/link';

type TProductBrandsProps = {
  id: string;
  products?: any[];
  className?: string;
  placement?: string | null;
  camperlab?: boolean;
  selectBrandProduct?: (id: string, products: string[]) => void;
};

type BrandElementProps = {
  onPress: () => void;
  type: 'link' | 'button';
  href?: string;
  className?: string;
};

function BrandElement({ onPress, type, href, className, children }: PropsWithChildren<BrandElementProps>) {
  if (type === 'button') {
    return (
      <Button variant="none" className={className} onPress={onPress}>
        {children}
      </Button>
    );
  }
  return (
    <Link variant="none" href={href} className={className} onPress={onPress}>
      {children}
    </Link>
  );
}

export function ProductBrands({ id, products = [], className = '', placement = null, camperlab = false, selectBrandProduct }: TProductBrandsProps) {
  const { t } = useI18n();
  const [emblaRef, emblaAPI] = useEmblaCarousel({
    skipSnaps: true,
    containScroll: 'trimSnaps',
  });
  const [previousButtonEnabled, setPreviousButtonEnabled] = useState(false);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [localId, setLocalId] = useState(id);

  const scrollPrevious = useCallback(() => emblaAPI && emblaAPI.scrollPrev(), [emblaAPI]);
  const scrollNext = useCallback(() => emblaAPI && emblaAPI.scrollNext(), [emblaAPI]);

  let filteredBrandProducts = [];

  if (Array.isArray(products) && products.length > 0) {
    filteredBrandProducts = products?.filter((item) => item.url);
  }

  const hasItems = filteredBrandProducts.length > 0;

  const handleClickBrandProduct = (productBrand: (typeof filteredBrandProducts)[0]) => {
    setLocalId(productBrand.id);
    dataLayerHandleEvent({
      event: 'item_photos',
      event_category: 'product detail',
      event_detail_1: 'open',
      event_detail_2: productBrand.image,
    });

    if (selectBrandProduct && typeof selectBrandProduct === 'function') {
      selectBrandProduct(
        productBrand.id,
        filteredBrandProducts.map((item) => item.id),
      );
    }
  };

  const handleClickButton = (event, buttonType) => {
    event.preventDefault();
    event.stopPropagation();

    const scrollFunction = buttonType === 'next' ? scrollNext : scrollPrevious;

    scrollFunction();
  };

  const onSelect = useCallback(() => {
    if (!emblaAPI) return;
    setPreviousButtonEnabled(emblaAPI.canScrollPrev());
    setNextButtonEnabled(emblaAPI.canScrollNext());
  }, [emblaAPI]);

  useEffect(() => {
    if (!emblaAPI) return;
    onSelect();
    emblaAPI.on('select', onSelect);
  }, [emblaAPI, onSelect]);

  if (!hasItems) return null;

  const wrapperClassName = placement === null ? '-mx-8 lab:-mx-2.5 overflow-hidden md:hidden' : 'overflow-hidden relative';
  const sliderClassName = placement === null ? 'mx-8 lab:mx-2.5' : '';
  const emblaStyle =
    placement === null ?
      {
        '--slide-height': '4.5rem',
        '--slide-gap': getDomainValue({ camper: '0.25rem', nnormal: 'camper', camperlab: '0.625rem' }),
      }
    : {
        '--slide-height': '3rem',
        '--slide-gap': '0.2rem',
      };

  // El width y height de las imagenes dependiendo de donde se vea el componente
  const camperWH = placement === 'grid' ? 48 : 72;

  const elementType = selectBrandProduct ? 'button' : 'link';

  return (
    <div className={className}>
      <p className="mb-2 text-base camper:hidden lab:mb-0 lab:text-sm lab:uppercase lab:lg:text-xs">{t('ficha.producto', 'colores', 'Colores')}:</p>
      {(
        getDomainValue({
          camper: true,
          camperlab: true,
          nnormal: false,
        })
      ) ?
        <div className={wrapperClassName}>
          {placement === 'grid' && nextButtonEnabled && <div className={`${styles.gradient} ${camperlab ? styles.gradientLab : styles.gradientWhite}`} />}
          <div style={emblaStyle} ref={emblaRef} className={sliderClassName}>
            <div className="-ml-[var(--slide-gap)] flex touch-pan-y touch-pinch-zoom">
              {filteredBrandProducts.map((productBrand, index) => (
                <div
                  key={`${productBrand.id}-${index}`}
                  className={`relative ml-[var(--slide-gap)] min-w-0 shrink-0 grow-0 basis-auto lab:h-15 lab:w-10 ${productBrand.id === localId ? 'after:absolute after:bottom-0 after:block after:h-0.5 after:w-full after:border-b-2 after:border-dark lab:bg-faint lab:after:h-full lab:after:border-0' : ''}`}
                >
                  <BrandElement href={productBrand.url} className="cursor-pointer" onPress={() => handleClickBrandProduct(productBrand)} type={elementType}>
                    <span className="sr-only">
                      {productBrand.name} - {productBrand.id}
                    </span>
                    <ProgressiveImg
                      loading={placement === 'grid' ? 'lazy' : 'eager'}
                      src={productBrand.image}
                      alt={`thumbnail image ${index} of ${productBrand.id}`}
                      height={getDomainValue({ camper: camperWH, camperlab: 60, nnormal: camperWH })}
                      width={getDomainValue({ camper: camperWH, camperlab: 40, nnormal: camperWH })}
                      className={styles.brandThumbnail}
                    />
                  </BrandElement>
                </div>
              ))}
            </div>
          </div>
          {placement === 'grid' && (
            <>
              {previousButtonEnabled && (
                <ChevronLeftSVG
                  onClick={(e) => handleClickButton(e, 'previous')}
                  className={`${styles.emblaButton} absolute left-[0.1rem] hidden cursor-pointer bg-[#fff] xl:flex`}
                />
              )}
              {nextButtonEnabled && (
                <ChevronLeftSVG
                  onClick={(e) => handleClickButton(e, 'next')}
                  className={`${styles.emblaButton} absolute right-[0.75rem] hidden rotate-180 cursor-pointer bg-[#fff] xl:flex`}
                />
              )}
            </>
          )}
        </div>
      : null}
      {placement !== 'grid' ?
        <div className="flex flex-row flex-wrap gap-2.5 lab:gap-1 camper:max-md:hidden lab:max-md:hidden md:gap-1">
          {filteredBrandProducts.map((productBrand, index) => (
            <div
              key={`${productBrand.id}-${index}`}
              className={`relative size-18 transition-colors lab:h-15 lab:w-10 lab:hover:bg-faint md:size-15 ${productBrand.id === localId ? 'after:absolute after:bottom-0 after:block after:h-0.5 after:w-full after:border-b-2 after:border-[var(--color-black)] lab:bg-faint lab:after:h-0 lab:after:border-0' : ''}`}
            >
              {/* @ts-ignore */}
              <BrandElement
                href={productBrand.url}
                className="h-full w-full cursor-pointer [&_img]:h-full [&_img]:w-full [&_picture]:flex [&_picture]:h-full [&_picture]:w-full"
                onPress={() => handleClickBrandProduct(productBrand)}
              >
                <span className="sr-only">
                  {productBrand.name} - {productBrand.id}
                </span>
                <ProgressiveImg
                  src={productBrand.image}
                  alt={`thumbnail image ${index} of ${productBrand.id}`}
                  className={`${styles.brandThumbnail} size-18 md:size-15`}
                />
              </BrandElement>
            </div>
          ))}
        </div>
      : null}
    </div>
  );
}
